// Feedback #829 (Hestia, 2026-09-17) — "Created By / Changed By" + History.
//
// One component for every place an event is shown: the Calendar detail
// popup, the matter's event list, and the To-Do list.  It used to live only
// inside Calendar.js, so the matter and To-Do views had no history at all.
//
// The two summary lines come straight off the event row (already loaded).
// The per-change list is fetched only when the user opens the expander —
// one GET /api/calendar/:id/history per opened event, never on render — so a
// long list costs no extra requests until someone asks.
//
// Props:
//   event        the event row (needs id, created_at, amicus_id,
//                created_by_name, updated_at, updated_by_name)
//   compact      list-row styling: no divider above, tighter spacing
//   showCreated  render the "Created …" line (default true).  The To-Do row
//                already shows the creator (👤), so it passes false.
import { useEffect, useRef, useState } from 'react'
import api from '../services/api'
import {
  createdLine, lastChangedLine, formatHistoryStamp, describeChangedFields,
  AUTOMATIC_CORRECTION_ACTOR,
} from '../utils/eventHistory'

// Contract C3 — who to name after "by" when a History entry has no person.
//
// A null user_name does NOT by itself mean the system made the change.
// audit_log.user_id is ON DELETE SET NULL, so when a staff account is
// permanently deleted (DELETE /api/team/:id/permanent — it has happened on
// production) every edit that person made comes back with user_name: null
// too.  The server sends 'Former user' only while the user_id is still on the
// row, which is exactly the case the FK takes away.  Reading every null as an
// automatic correction would credit a human's edit to Omnia, in a history
// view a bar auditor can read.
//
// So Omnia is named ONLY for an action the system itself writes — today the
// EVT_RETYPE rollup for the 418 Amicus events whose type the importer had
// collapsed (code review of 9ff73f1, finding #6), whose audit row is written
// with no user_id by design.  Every other person-less entry was made by a
// person who has since been deleted, and reads "Former user", the same label
// the server uses for a deleted user whose id survived.  Either way the entry
// never shows "null" / "undefined" or a bare "by".  EVT_ATTENDEES (the
// attendee restore, api/scripts/backfill-event-attendees-kind.sql) is a data
// repair too: it records its admin actor, and reads as Omnia only if that
// account is later deleted.
const SYSTEM_HISTORY_ACTIONS = new Set(['EVT_RETYPE', 'EVT_ATTENDEES'])
const DELETED_USER_ACTOR = 'Former user'
const historyActor = (entry) => {
  const name = entry?.user_name
  if (typeof name === 'string' && name.trim() !== '') return name
  return SYSTEM_HISTORY_ACTIONS.has(entry?.action_code) ? AUTOMATIC_CORRECTION_ACTOR : DELETED_USER_ACTOR
}

export default function EventHistory ({ event, compact = false, showCreated = true }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  // Code review of 9ff73f1, finding #5.  Every history request is stamped
  // with the generation current when it was sent.  The effect below moves the
  // generation on (and aborts the request) whenever the event's id or
  // updated_at changes, and on unmount — so a response for an OLD version of
  // the event is dropped instead of being stored as the new version's
  // history.  Without it, a request still in flight when the event was
  // edited landed after the reset below, set `data`, and — because `data`
  // was then set — reopening never refetched: the panel kept showing history
  // that stopped before the change.
  const genRef = useRef(0)
  const abortRef = useRef(null)

  const eventId = event?.id
  const updatedAt = event?.updated_at
  // A list row stays mounted while its event changes underneath it (edit,
  // complete, reschedule).  Drop the cached list so the next open re-reads it
  // instead of showing history that no longer ends where the event is now.
  // `loading` is reset too: an old request still out must not block the
  // fresh one the next open sends (toggle returns early while loading).
  useEffect(() => {
    setData(null)
    setOpen(false)
    setError('')
    setLoading(false)
    return () => {
      genRef.current += 1
      if (abortRef.current) {
        abortRef.current.abort()
        abortRef.current = null
      }
    }
  }, [eventId, updatedAt])

  if (!event || !event.id) return null

  const created = showCreated ? createdLine(event) : ''
  const changed = lastChangedLine(event)

  const toggle = async (e) => {
    // Rows in the matter / To-Do lists can be clickable; opening history
    // must not also trigger the row.
    if (e) e.stopPropagation()
    const next = !open
    setOpen(next)
    if (!next || data || loading) return
    const gen = genRef.current
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)
    setError('')
    try {
      const res = await api.get(`/api/calendar/${event.id}/history`, { signal: controller.signal })
      if (gen !== genRef.current) return
      setData(res.data)
    } catch (err) {
      // An aborted request always belongs to an old generation, so it never
      // reaches this message.
      if (gen !== genRef.current) return
      setError(err?.response?.data?.error || 'Could not load history.')
    } finally {
      // Only the current request may end the loading state — an old one
      // finishing must not hide "Loading…" for the request that replaced it.
      if (gen === genRef.current) {
        setLoading(false)
        if (abortRef.current === controller) abortRef.current = null
      }
    }
  }

  const since = data?.recorded_since ? formatHistoryStamp(data.recorded_since).split(' ')[0] : ''
  const sinceNote = since
    ? `History is recorded from ${since} onward — changes made before then were not tracked.`
    : 'History is recorded from when this feature was turned on — earlier changes were not tracked.'

  return (
    <div className={compact ? 'mt-1' : 'border-t border-gray-100 pt-3'}>
      {created && <p className="text-xs text-gray-500">{created}</p>}
      {changed && <p className={`text-xs text-gray-500 ${created ? 'mt-0.5' : ''}`}>{changed}</p>}
      <button type="button" onClick={toggle} aria-expanded={open}
        className={`${compact ? 'mt-0.5' : 'mt-1.5'} text-xs font-medium text-primary-600 hover:text-primary-700`}>
        {open ? '▾ Hide history' : '▸ History'}
      </button>
      {open && (
        <div className={compact ? 'mt-1' : 'mt-2'}>
          {loading && <p className="text-xs text-gray-400">Loading history…</p>}
          {error && <p className="text-xs text-red-600">{error}</p>}
          {data && (
            <>
              {data.history.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No changes recorded yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {data.history.map((h, i) => {
                    const fields = describeChangedFields(h.fields_changed)
                    return (
                      <li key={i} className="text-xs text-gray-600">
                        <span className="font-medium text-gray-700">{formatHistoryStamp(h.changed_at)}</span>
                        {' — '}{h.action} by {historyActor(h)}
                        {fields && <span className="text-gray-500"> ({fields})</span>}
                      </li>
                    )
                  })}
                </ul>
              )}
              <p className="text-xs text-gray-400 italic mt-2">{sinceNote}</p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
